import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePipelineActions } from "@/components/shared/PipelineTable";
import { useSendProjectToEnhancements } from "@/hooks/useSupportQueue";
import { useCan } from "@/lib/roles";
import type { Project } from "@/types/project";

/**
 * The go / no-go on a REQUESTED project, rendered on its board row in
 * New / unscoped (2026-09-29: the board is the single funnel; Intake is
 * the service-desk inbox). Kickstart brief state rides along so the
 * PMO decides with the brief in view. PMO-only; others see "PMO decides".
 */
export function RequestDecision({ p, compact = false }: { p: Project; compact?: boolean }) {
  const { isPmo } = useCan();
  const { approve, decline, setAgenda } = usePipelineActions();
  if (!isPmo) return <span className="text-[11px] text-ink-subtle" title="Approving or declining is a PMO decision">PMO decides</span>;
  return (
    <span className="flex items-center gap-2" data-testid="request-decision" onClick={(e) => e.stopPropagation()}>
      <KickstartBadge projectId={p.id} status={p.brief_status} />
      {/* CFO approval-meeting flag (2026-09-30): lived on the retired Intake table. */}
      <button
        type="button"
        onClick={() => setAgenda(p.id, !p.cfo_agenda)}
        className={p.cfo_agenda
          ? "inline-flex items-center gap-1 rounded-full bg-brand-navy-100 dark:bg-brand-navy-500/30 px-2 py-0.5 text-[10px] font-semibold text-brand-navy-700 dark:text-brand-navy-200"
          : "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-ink-subtle ring-1 ring-line hover:text-ink-3"}
        title={p.cfo_agenda ? "Scheduled for the CFO approval meeting — click to remove" : "Schedule for the CFO approval meeting"}
        data-testid="agenda-toggle"
      >
        <CalendarCheck className="h-3 w-3" /> {p.cfo_agenda ? "On agenda" : "Agenda"}
      </button>
      <button
        type="button"
        onClick={() => approve(p.id)}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
        title="Approve — moves to the bottom of Next up"
      >
        <Check className="h-3 w-3" /> {compact ? "" : "Approve"}
      </button>
      <DeclineButton onDecline={() => decline(p.id)} />
      <SendToEnhancementsButton p={p} />
    </span>
  );
}

/** Smaller than a project (2026-10-01): moves it to the Small Enhancements
 *  list, keeping the ticket link. Requested or Approved rows; an approved
 *  one lands already approved. */
export function SendToEnhancementsButton({ p }: { p: Project }) {
  const { isPmo } = useCan();
  const send = useSendProjectToEnhancements();
  if (!isPmo) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); send.mutate(p.id, { onSuccess: () => toast.success(`${p.name} moved to Small Enhancements`), onError: (err: any) => toast.error(err?.response?.data?.detail ?? "Couldn't move it") }); }}
      disabled={send.isPending}
      className="text-[11px] text-ink-subtle hover:text-brand-navy-700 dark:hover:text-brand-navy-300 hover:underline disabled:opacity-50"
      title="This is smaller than a project — send it to the Small Enhancements list"
      data-testid="send-to-enhancements"
    >
      → Enhancement
    </button>
  );
}

export function KickstartBadge({ projectId, status }: { projectId: string; status?: string | null }) {
  const qc = useQueryClient();
  const markReceived = useMutation({
    mutationFn: () => api.put(`/briefs/${projectId}`, { status: "submitted" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["forecast"] });
      toast.success("Kickstart brief marked received");
    },
    onError: () => toast.error("Couldn't update the brief"),
  });
  const s = (status ?? "").toLowerCase();
  if (s === "not_required") return <span className="text-[10px] text-ink-subtle">N/A</span>;
  if (s === "submitted" || s === "approved") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300" title="Kickstart brief received">
        <Check className="h-3 w-3" /> Brief
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => markReceived.mutate()}
      disabled={markReceived.isPending}
      className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30 transition-colors hover:bg-amber-100 disabled:opacity-50"
      title="No kickstart brief yet — click when it arrives"
    >
      Mark received
    </button>
  );
}

/** Two-step decline so a stray click can't terminate a request. */
export function DeclineButton({ onDecline }: { onDecline: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} className="text-[11px] font-medium text-ink-subtle hover:text-rose-600 hover:underline">
        Decline
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <button type="button" onClick={onDecline} className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700">Decline?</button>
      <button type="button" onClick={() => setConfirming(false)} className="text-[11px] text-ink-subtle hover:underline">Cancel</button>
    </span>
  );
}
